import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  ArrowLeft, 
  Landmark, 
  Plus, 
  ChevronRight,
  Trash2,
  Send,
  Loader2,
  Building,
  Globe
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

// Bank fields by currency/country
const BANK_FIELDS = {
  JMD: {
    country: "Jamaica",
    fields: [
      { name: "bank_name", label: "Bank Name", placeholder: "e.g., NCB, Scotiabank, JMMB", required: true },
      { name: "account_holder", label: "Account Holder Name", placeholder: "Full name on account", required: true },
      { name: "account_number", label: "Account Number", placeholder: "Enter account number", required: true },
      { name: "branch_code", label: "Branch Code", placeholder: "Branch transit number", required: false },
    ]
  },
  USD: {
    country: "United States",
    fields: [
      { name: "bank_name", label: "Bank Name", placeholder: "e.g., Chase, Bank of America", required: true },
      { name: "account_holder", label: "Account Holder Name", placeholder: "Full name on account", required: true },
      { name: "routing_number", label: "Routing Number (ABA)", placeholder: "9-digit routing number", required: true },
      { name: "account_number", label: "Account Number", placeholder: "Enter account number", required: true },
      { name: "account_type", label: "Account Type", placeholder: "Checking or Savings", required: true, type: "select", options: ["Checking", "Savings"] },
    ]
  },
  GBP: {
    country: "United Kingdom",
    fields: [
      { name: "bank_name", label: "Bank Name", placeholder: "e.g., Barclays, HSBC, Lloyds", required: true },
      { name: "account_holder", label: "Account Holder Name", placeholder: "Full name on account", required: true },
      { name: "sort_code", label: "Sort Code", placeholder: "6-digit sort code (XX-XX-XX)", required: true },
      { name: "account_number", label: "Account Number", placeholder: "8-digit account number", required: true },
    ]
  },
  CAD: {
    country: "Canada",
    fields: [
      { name: "bank_name", label: "Bank Name", placeholder: "e.g., TD, RBC, Scotiabank", required: true },
      { name: "account_holder", label: "Account Holder Name", placeholder: "Full name on account", required: true },
      { name: "institution_number", label: "Institution Number", placeholder: "3-digit institution number", required: true },
      { name: "transit_number", label: "Transit Number", placeholder: "5-digit transit number", required: true },
      { name: "account_number", label: "Account Number", placeholder: "Enter account number", required: true },
    ]
  },
  EUR: {
    country: "Europe (SEPA)",
    fields: [
      { name: "bank_name", label: "Bank Name", placeholder: "e.g., Deutsche Bank, BNP Paribas", required: true },
      { name: "account_holder", label: "Account Holder Name", placeholder: "Full name on account", required: true },
      { name: "iban", label: "IBAN", placeholder: "International Bank Account Number", required: true },
      { name: "bic_swift", label: "BIC/SWIFT Code", placeholder: "8 or 11 character code", required: false },
    ]
  },
};

const BankAccounts = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("JMD");
  const [formData, setFormData] = useState({});

  // Fetch saved bank accounts
  const { data: accountsData, isLoading } = useQuery({
    queryKey: ["bankAccounts"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/bank-accounts`);
        return response.data;
      } catch {
        return { accounts: [] };
      }
    },
  });

  // Add bank account mutation
  const addAccountMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/bank-accounts`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["bankAccounts"]);
      toast.success("Bank account added!");
      setShowAddDialog(false);
      setFormData({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to add bank account");
    }
  });

  // Delete bank account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: async (accountId) => {
      await axios.delete(`${API}/bank-accounts/${accountId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["bankAccounts"]);
      toast.success("Bank account removed");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to remove bank account");
    }
  });

  const accounts = accountsData?.accounts || [];
  const bankFields = BANK_FIELDS[selectedCurrency];

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleAddAccount = () => {
    // Validate required fields
    const requiredFields = bankFields.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !formData[f.name]?.trim());
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.map(f => f.label).join(", ")}`);
      return;
    }

    addAccountMutation.mutate({
      currency: selectedCurrency,
      country: bankFields.country,
      ...formData
    });
  };

  const resetForm = () => {
    setFormData({});
    setSelectedCurrency("JMD");
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="bank-accounts-page">
      <header className="px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/explore")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Bank Accounts</h1>
      </header>

      <main className="px-6 space-y-6">
        {/* Add Button */}
        <Button
          onClick={() => setShowAddDialog(true)}
          className="w-full btn-primary h-12"
          data-testid="add-bank-account-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Bank Account
        </Button>

        {/* Accounts List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Loading accounts...</p>
            </div>
          ) : accounts.length > 0 ? (
            accounts.map((account) => (
              <div
                key={account.account_id}
                className="glass rounded-2xl p-4"
                data-testid={`bank-account-${account.account_id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  {/* Bank Icon */}
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Landmark className="w-6 h-6 text-blue-400" />
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{account.bank_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {account.account_holder}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ••••{account.account_number?.slice(-4)} • {account.currency}
                    </p>
                  </div>
                  
                  {/* Currency Badge */}
                  <div className="px-2 py-1 rounded-lg bg-white/10 text-xs font-medium">
                    {account.currency}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 btn-secondary"
                    onClick={() => navigate("/remittance", { state: { bankAccount: account } })}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Send Money
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => deleteAccountMutation.mutate(account.account_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 glass rounded-2xl">
              <Landmark className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="font-medium text-foreground">No bank accounts</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add external bank accounts to send money
              </p>
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="glass rounded-xl p-4 border border-white/10">
          <p className="text-xs text-muted-foreground">
            <Globe className="w-4 h-4 inline mr-1 text-blue-400" />
            Bank transfers may take 1-3 business days to complete. International transfers may have additional fees.
          </p>
        </div>
      </main>

      {/* Add Bank Account Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="glass-heavy border-white/[0.08] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Bank Account</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Currency Selection */}
            <div>
              <Label>Currency / Country</Label>
              <Select value={selectedCurrency} onValueChange={(val) => { setSelectedCurrency(val); setFormData({}); }}>
                <SelectTrigger className="input-glass mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BANK_FIELDS).map(([currency, config]) => (
                    <SelectItem key={currency} value={currency}>
                      {currency} - {config.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic Fields */}
            {bankFields.fields.map((field) => (
              <div key={field.name}>
                <Label>
                  {field.label}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </Label>
                {field.type === "select" ? (
                  <Select 
                    value={formData[field.name] || ""} 
                    onValueChange={(val) => handleFieldChange(field.name, val)}
                  >
                    <SelectTrigger className="input-glass mt-2">
                      <SelectValue placeholder={field.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder={field.placeholder}
                    className="input-glass mt-2"
                    value={formData[field.name] || ""}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    data-testid={`field-${field.name}`}
                  />
                )}
              </div>
            ))}

            {/* Nickname (optional) */}
            <div>
              <Label>Nickname (optional)</Label>
              <Input
                placeholder="e.g., My Savings Account"
                className="input-glass mt-2"
                value={formData.nickname || ""}
                onChange={(e) => handleFieldChange("nickname", e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => { setShowAddDialog(false); resetForm(); }}
              className="btn-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddAccount}
              disabled={addAccountMutation.isPending}
              className="btn-primary"
            >
              {addAccountMutation.isPending ? "Adding..." : "Add Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NavBar active="wallet" />
    </div>
  );
};

export default BankAccounts;
